import React, { memo, useState, useEffect } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import MoonLoader from "react-spinners/MoonLoader";
import { IconChartBar, IconRefresh, IconUsers, IconClock, IconCheck, IconArrowUpRight } from "@tabler/icons-react";
import { ServiceCenterAPI } from "../../services";
import { formatDuration } from "../../utils/formatters";
import "./Modals.css";

/**
 * Modal de estatísticas de atendimento
 */
const ServiceStatsModal = memo(({ show, onHide }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [period, setPeriod] = useState("today"); // today, week, month

  // Carregar estatísticas
  const loadStats = async () => {
    setIsLoading(true);
    try {
      const data = await ServiceCenterAPI.getStats(period);
      setStats(data);
    } catch (error) {
      console.error("Erro ao carregar estatísticas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadStats();
    }
  }, [show, period]);

  // Card de estatística
  const StatCard = ({ icon, label, value, color, subvalue }) => (
    <div className="sc-stat-card" style={{ borderColor: color }}>
      <div className="sc-stat-card__icon" style={{ backgroundColor: `${color}20`, color }}>
        {icon}
      </div>
      <div className="sc-stat-card__content">
        <span className="sc-stat-card__value">{value}</span>
        <span className="sc-stat-card__label">{label}</span>
        {subvalue && <span className="sc-stat-card__subvalue">{subvalue}</span>}
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="sc-modal sc-modal--full"
    >
      <Modal.Header closeButton>
        <Modal.Title className="sc-modal__title">
          <IconChartBar size={22} />
          Estatísticas de Atendimento
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Filtro de período */}
        <div className="sc-modal__toolbar">
          <div className="sc-modal__period-selector">
            <button
              className={`sc-modal__period-btn ${period === "today" ? "active" : ""}`}
              onClick={() => setPeriod("today")}
            >
              Hoje
            </button>
            <button
              className={`sc-modal__period-btn ${period === "week" ? "active" : ""}`}
              onClick={() => setPeriod("week")}
            >
              Esta semana
            </button>
            <button
              className={`sc-modal__period-btn ${period === "month" ? "active" : ""}`}
              onClick={() => setPeriod("month")}
            >
              Este mês
            </button>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={loadStats}
            disabled={isLoading}
          >
            <IconRefresh size={16} className={isLoading ? "spin" : ""} />
            Atualizar
          </Button>
        </div>

        {/* Estatísticas */}
        {isLoading ? (
          <div className="sc-modal__loading">
            <MoonLoader size={30} color="#3b82f6" />
            <span>Carregando estatísticas...</span>
          </div>
        ) : stats ? (
          <div className="sc-modal__stats">
            <Row className="g-3">
              <Col xs={6} md={3}>
                <StatCard
                  icon={<IconUsers size={24} />}
                  label="Total de Atendimentos"
                  value={stats.total_services || 0}
                  color="#3b82f6"
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  icon={<IconCheck size={24} />}
                  label="Finalizados"
                  value={stats.finished_services || 0}
                  color="#06b776"
                  subvalue={stats.total_services ? `${Math.round((stats.finished_services / stats.total_services) * 100)}%` : null}
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  icon={<IconArrowUpRight size={24} />}
                  label="Transferidos"
                  value={stats.transferred_services || 0}
                  color="#06b6d4"
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  icon={<IconClock size={24} />}
                  label="Tempo Médio"
                  value={stats.avg_duration ? formatDuration(stats.avg_duration * 1000) : "N/A"}
                  color="#f59e0b"
                />
              </Col>
            </Row>

            {/* Métricas adicionais */}
            {stats.by_attendant && stats.by_attendant.length > 0 && (
              <div className="sc-modal__section">
                <h5 className="sc-modal__section-title">Por Atendente</h5>
                <div className="sc-modal__table">
                  <div className="sc-modal__table-header">
                    <span>Atendente</span>
                    <span>Atendimentos</span>
                    <span>Finalizados</span>
                    <span>Tempo Médio</span>
                  </div>
                  {stats.by_attendant.map((item, index) => (
                    <div key={index} className="sc-modal__table-row">
                      <span>{item.name || item.username}</span>
                      <span>{item.total}</span>
                      <span>{item.finished}</span>
                      <span>{item.avg_duration ? formatDuration(item.avg_duration * 1000) : "N/A"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Por departamento */}
            {stats.by_department && stats.by_department.length > 0 && (
              <div className="sc-modal__section">
                <h5 className="sc-modal__section-title">Por Departamento</h5>
                <div className="sc-modal__table">
                  <div className="sc-modal__table-header">
                    <span>Departamento</span>
                    <span>Atendimentos</span>
                    <span>Na Fila</span>
                  </div>
                  {stats.by_department.map((item, index) => (
                    <div key={index} className="sc-modal__table-row">
                      <span>{item.name}</span>
                      <span>{item.total}</span>
                      <span>{item.in_queue || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="sc-modal__empty">
            <span>Não foi possível carregar as estatísticas</span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

ServiceStatsModal.displayName = "ServiceStatsModal";

export default ServiceStatsModal;
